/**
 * Loads and persists POC assignment group metadata from Firestore for the
 * Groups → Update modal (`metadata/poc_assignment_groups`).
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { FirestoreService } from "../../../../../api/firebase/firestoreService";
import {
  FirestoreCollectionReference,
  FirestoreDocumentReference,
} from "../../../../../api/firebase/references";
import type {
  PocAssignmentGroupEntry,
  PocAssignmentGroupsMetadataDoc,
} from "../../../../../data-model/pocAssignmentGroupsMetadata";
import { ModalId } from "../../../../../enums/modalId";
import { closeModal as closeReduxModal } from "../../../../../redux/slice/modalSliceNew";
import { RootState } from "../../../../../redux/store/store";
import { POC_ASSIGNMENT_GROUPS_DOC_ID } from "../../pocAssignment/pocAssignmentUtils";

/** Client-side row identity for list rendering and inline edit. */
export interface PocAssignmentGroupDraftRow extends PocAssignmentGroupEntry {
  key: string;
}

const toDraftRows = (
  entries: PocAssignmentGroupEntry[],
): PocAssignmentGroupDraftRow[] =>
  entries.map((e) => ({
    key: uuidv4(),
    group_name: e.group_name,
    batches: Array.isArray(e.batches) ? [...e.batches] : [],
  }));

const rowsEqual = (
  a: PocAssignmentGroupDraftRow[],
  b: PocAssignmentGroupDraftRow[],
) =>
  JSON.stringify(
    a.map(({ group_name, batches }) => ({ group_name, batches })),
  ) ===
  JSON.stringify(b.map(({ group_name, batches }) => ({ group_name, batches })));

const stripKeys = (
  rows: PocAssignmentGroupDraftRow[],
): PocAssignmentGroupEntry[] =>
  rows.map(({ group_name, batches }) => ({
    group_name,
    batches: [...batches],
  }));

/**
 * State and handlers for editing `poc_assignment_groups` metadata while the
 * update-groups modal is open.
 *
 * @returns Draft rows, loading flags, dirty detection, and CRUD/save handlers.
 */
export function usePocAssignmentGroupsMetadataModal() {
  const dispatch = useDispatch();
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const openedModalId = useSelector(
    (state: RootState) => state.modalSliceNew.openedModalId,
  );

  const [rows, setRows] = useState<PocAssignmentGroupDraftRow[]>([]);
  const [baselineRows, setBaselineRows] = useState<
    PocAssignmentGroupDraftRow[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);

  const isOpen = openedModalId === ModalId.UPDATE_GROUP_MODAL;

  const loadFromServer = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const doc =
        await FirestoreService.getDocument<PocAssignmentGroupsMetadataDoc>(
          FirestoreDocumentReference.pharmacyOrderUpdatesPocAssignmentGroups(),
        );
      const next = toDraftRows(doc.data ?? []);
      setRows(next);
      setBaselineRows(
        next.map((r) => ({
          key: r.key,
          group_name: r.group_name,
          batches: [...r.batches],
        })),
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (message.includes("does not exist")) {
        setRows([]);
        setBaselineRows([]);
      } else {
        setLoadError(message);
        toast.error("Failed to load groups metadata.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setEditingKey(null);
    void loadFromServer();
  }, [isOpen, loadFromServer]);

  const isDirty = useMemo(
    () => !rowsEqual(rows, baselineRows),
    [rows, baselineRows],
  );

  const addRow = useCallback(() => {
    setRows((prev) => [
      ...prev,
      { key: uuidv4(), group_name: "", batches: [] },
    ]);
    setEditingKey(null);
  }, []);

  const removeRow = useCallback((key: string) => {
    setRows((prev) => prev.filter((r) => r.key !== key));
    setEditingKey((cur) => (cur === key ? null : cur));
  }, []);

  const updateRow = useCallback(
    (key: string, patch: Partial<PocAssignmentGroupEntry>) => {
      setRows((prev) =>
        prev.map((r) => {
          if (r.key !== key) return r;
          return {
            ...r,
            ...(patch.group_name !== undefined && {
              group_name: patch.group_name,
            }),
            ...(patch.batches !== undefined && { batches: [...patch.batches] }),
          };
        }),
      );
    },
    [],
  );

  const dismissRedux = useCallback(() => {
    dispatch(closeReduxModal());
  }, [dispatch]);

  const resetLocal = useCallback(() => {
    setRows([]);
    setBaselineRows([]);
    setLoadError(null);
    setEditingKey(null);
  }, []);

  /**
   * Validates draft rows, writes metadata to Firestore, and refreshes baseline.
   *
   * @throws Never; surfaces errors via toast and returns false on failure.
   * @returns True when the document was saved successfully.
   */
  const save = useCallback(async (): Promise<boolean> => {
    const trimmed = rows.map((r) => ({
      ...r,
      group_name: r.group_name.trim(),
    }));

    const names = trimmed.map((r) => r.group_name);
    if (names.some((n) => n === "")) {
      toast.error("Each group must have a name.");
      return false;
    }
    if (new Set(names).size !== names.length) {
      toast.error("Group names must be unique.");
      return false;
    }

    setSaving(true);
    try {
      const nowIso = new Date().toISOString();
      const payload: PocAssignmentGroupsMetadataDoc = {
        id: POC_ASSIGNMENT_GROUPS_DOC_ID,
        data: stripKeys(trimmed),
        updated_at: nowIso,
        ...(user?.email != null &&
          user.email !== "" && { updated_by: user.email }),
      };

      await FirestoreService.setDocument(
        FirestoreCollectionReference.pharmacyOrderUpdatesMetadata(),
        POC_ASSIGNMENT_GROUPS_DOC_ID,
        payload,
      );

      const next = trimmed.map((r) => ({
        ...r,
        batches: [...r.batches],
      }));
      setRows(next);
      setBaselineRows(
        next.map((r) => ({
          key: r.key,
          group_name: r.group_name,
          batches: [...r.batches],
        })),
      );
      toast.success("Groups saved");
      return true;
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save groups. Please try again.",
      );
      return false;
    } finally {
      setSaving(false);
    }
  }, [rows, user?.email]);

  return {
    isOpen,
    rows,
    loading,
    saving,
    loadError,
    isDirty,
    editingKey,
    setEditingKey,
    addRow,
    removeRow,
    updateRow,
    save,
    reload: loadFromServer,
    dismissRedux,
    resetLocal,
  };
}
